import React, { useState, useRef, useMemo } from 'react';
import RoleCard from './role_card';

const RecommendedRoles = ({
  recommendedRoles = [],
  recommendedRolesLoading = false,
  recommendedRolesError = null,
  onSaveToggle,
  shortlistedRoles,
  shortlistedRolesLoaded,
  onApplyNow,
  appliedRoles,
  appliedRolesLoaded,
  interviewingRoles,
  interviewingRolesLoaded,
  unsuccessfulRoles,
  unsuccessfulRolesLoaded,
  applicationPendingRoles,
  applicationPendingRolesLoaded,
  candidateData,
  onRequireVideo
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const scrollContainerRef = useRef(null);

  const loading = recommendedRolesLoading;
  const error = recommendedRolesError;
  const roles = Array.isArray(recommendedRoles) ? recommendedRoles : [];

  const filteredRecommendedRoles = useMemo(() => {
    if (roles.length === 0) {
      return [];
    }

    const filtered = roles.filter(role => {
      const roleId = role.roleId;
      
      // Exclude if already applied, interviewing, rejected, or pending
      if (appliedRoles.includes(roleId)) return false;
      if (interviewingRoles.includes(roleId)) return false;
      if (unsuccessfulRoles.includes(roleId)) return false;
      if (applicationPendingRoles.includes(roleId)) return false;
      
      return true;
    });

    console.log(`Filtered recommendations: ${filtered.length} of ${roles.length} are shown`);
    return filtered;
  }, [roles, appliedRoles, interviewingRoles, unsuccessfulRoles, applicationPendingRoles]);

  // Handle scrolling with buttons
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -370 : 370;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  
  // If loading, show skeleton placeholders
  if (loading) {
    return (
      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center mb-4 sm:mb-6 px-4 sm:px-0">
          <div className="h-6 bg-gray-200 rounded w-1/4 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse"></div>
        </div>
        <div className="relative">
          <div className="flex overflow-x-hidden space-x-4 sm:space-x-6 px-4 sm:px-0">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex-shrink-0 w-[400px]">
                <div className="bg-gray-100 rounded-xl shadow-sm p-4 animate-pulse">
                  <div className="h-24 bg-gray-200 rounded-lg mb-3"></div>
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // If error or no recommendations, don't render the component
  if (error || filteredRecommendedRoles.length === 0) {
     console.log('RecommendedRoles not rendering:', error ? `Error: ${error}` : 'No recommendations or all are filtered out');
     return null; 
  }
  
  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex justify-between items-center mb-4 sm:mb-6 px-4 sm:px-0">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Your Top Matches</h2>
      </div>

      <div className="relative">
        <button 
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 z-20 transform -translate-y-1/2 -translate-x-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition duration-150 ease-in-out hidden sm:block"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto space-x-4 sm:space-x-6 pb-4 scroll-smooth scrollbar-hide snap-x snap-mandatory px-4 sm:px-0"
        >
          {filteredRecommendedRoles.map((item) => {
            console.log(`Rendering recommendation for: ${item.roleTitle} - Quality: ${item.matchQuality}`);
            
            const roleData = {
              id: item.roleId,
              "Role Title": item.roleTitle,
              "Company": item.company, 
              "Name (from Startup)": item["Name (from Startup)"] || item.company, 
              "Role Status": "Live", 
              "Job Description text": item.jobDescriptionText || "",
              "Specific Experience": item.specificExperience || "",
              "Role Cover Image (from Startup)": item.roleImage || null,
              "Location (from Startup)": item.locationFromStartup || "",
              "Working style category": item.workingStyleCategory || "",
              "Compensation": item.compensation || "",
              "Team Size (from Startup)": item.teamSize || "",
              "Funding Stage (from Startup)": item.fundingStage || "",
              "Startup Description (from Startup)": item.startupDescription || "",
              "Spotlight": item.roleFields?.["Spotlight"] || [], 
              "URL ID": item.urlId || item.roleId,
              "stage": item.stage || "",
              "Stream": item.stream || [],
              "spotlightDetails": item.spotlightDetails || [],
              "Role Headcount": item.roleHeadcount || item["Role Headcount"] || "",
            };
            
            // Determine badge color based on match quality
            let badgeColor = "bg-yellow-500";
            let badgeTextColor = "text-yellow-900";
            let badgeBgColor = "bg-yellow-100";

            if (item.matchQuality === "Excellent Match") {
              badgeColor = "bg-emerald-500";
              badgeTextColor = "text-emerald-900";
              badgeBgColor = "bg-emerald-100";
            } else if (item.matchQuality === "Fair Match" || !item.matchQuality) {
              badgeColor = "bg-gray-400";
              badgeTextColor = "text-gray-700";
              badgeBgColor = "bg-gray-100";
            }

            console.log(`Processed role data for ${item.roleTitle}: ID ${roleData.id}, Match ${item.matchScore}, Quality ${item.matchQuality}`);
            
            return (
              <div 
                key={item.roleId}
                className="flex-shrink-0 w-[400px] snap-start"
              >
                <div className="overflow-hidden rounded-xl shadow-md border border-gray-100 bg-white relative h-full flex flex-col">
                  <RoleCard 
                    role={roleData}
                    currentlyPlaying={currentlyPlaying}
                    setCurrentlyPlaying={setCurrentlyPlaying}
                    viewMode="grid"
                    onSaveToggle={onSaveToggle}
                    shortlistedRoles={shortlistedRoles}
                    shortlistedRolesLoaded={shortlistedRolesLoaded}
                    onApplyNow={onApplyNow}
                    appliedRoles={appliedRoles}
                    appliedRolesLoaded={appliedRolesLoaded}
                    interviewingRoles={interviewingRoles}
                    interviewingRolesLoaded={interviewingRolesLoaded}
                    unsuccessfulRoles={unsuccessfulRoles}
                    unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                    applicationPendingRoles={applicationPendingRoles}
                    applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                    isRecommended={true} 
                    matchScore={item.matchScore}
                    matchQuality={item.matchQuality}
                    candidateData={candidateData}
                    onRequireVideo={onRequireVideo}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <button 
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 z-20 transform -translate-y-1/2 translate-x-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition duration-150 ease-in-out hidden sm:block"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RecommendedRoles;